import { differenceInCalendarDays } from 'date-fns';
import { useCallback, useMemo, useState } from 'react';

import { SUBSCRIPTIONS_HIDDEN } from '@/pages/SettingsPage/components/settings-flags';
import {
  EDIT_SUBSCRIPTION,
  planManageableServices,
} from '@/pages/SubscriptionSettingsPage/subscription-access';
import { planName, renewalOf } from '@/pages/SubscriptionSettingsPage/subscription-display';
import { useActiveAccount } from '@/store/active-account/active-account.store';
import { useAuth } from '@/store/auth/auth.store';
import { useSubscriptionsQuery } from '@/store/subscriptions/subscriptions.store';
import { PaymentStatus } from '@/store/subscriptions/subscriptions.types';

/** The three reminders: from a month out, from a week out, and on the day itself. */
export type TrialStage = 'month' | 'week' | 'lastDay';

export type TrialReminder = {
  stage: TrialStage;
  /** Calendar days until the trial ends; 0 on the last day. */
  daysLeft: number;
  endsAt: number;
  /** The vessel whose trial it is — the one open in the account switcher. */
  serviceId: string;
  /** "Command", "Navigate" — the plan the trial is of. */
  planLabel: string;
};

const MONTH_DAYS = 30;
const WEEK_DAYS = 7;
const DAY_MS = 24 * 60 * 60 * 1000;

const stageOf = (daysLeft: number): TrialStage | null => {
  if (daysLeft <= 0) return 'lastDay';
  if (daysLeft <= WEEK_DAYS) return 'week';
  if (daysLeft <= MONTH_DAYS) return 'month';
  return null;
};

const DISMISSED_KEY = 'trial_banner_dismissed';

/**
 * A dismissal holds for one stage of one trial: the month reminder stays closed until the
 * week one is due, the week one comes back the next day. A new trial end — extended by a
 * referral, or another vessel's trial — is a different reminder and shows again.
 */
type Dismissal = { serviceId: string; endsAt: number; stage: TrialStage; until: number | null };

const readDismissal = (): Dismissal | null => {
  try {
    const raw = localStorage.getItem(DISMISSED_KEY);
    return raw ? (JSON.parse(raw) as Dismissal) : null;
  } catch {
    return null;
  }
};

const isDismissed = (reminder: TrialReminder, dismissal: Dismissal | null, now: number) =>
  !!dismissal &&
  dismissal.serviceId === reminder.serviceId &&
  dismissal.endsAt === reminder.endsAt &&
  dismissal.stage === reminder.stage &&
  (dismissal.until === null || now < dismissal.until);

/**
 * The trial reminder for the vessel open in the account switcher, or `null` when there is
 * nothing to remind the user of.
 *
 * Only the active vessel counts: the banner speaks about the vessel on screen, and switching
 * to another one switches the reminder with it. Viewers who can only read that vessel's plan
 * are not asked to pick one, and a service-provider account has no vessel plan at all.
 */
export const useTrialReminder = () => {
  const services = useAuth((state) => state.session?.services);
  const activeServiceId = useActiveAccount((state) => state.serviceId);
  const activeServiceType = useActiveAccount((state) => state.serviceType);
  const serviceIds = useMemo(
    () =>
      activeServiceType === 'vessel'
        ? planManageableServices(services)
            .filter(
              (service) =>
                service.service_id === activeServiceId &&
                service.rights?.includes(EDIT_SUBSCRIPTION),
            )
            .map((service) => service.service_id)
        : [],
    [services, activeServiceId, activeServiceType],
  );
  const { byServiceId } = useSubscriptionsQuery(SUBSCRIPTIONS_HIDDEN ? [] : serviceIds);
  const [dismissal, setDismissal] = useState(readDismissal);
  // Read once per mount: the stage moves on a day boundary, and every page change remounts
  // the header that carries the banner.
  const [now] = useState(() => Date.now());

  const reminder = useMemo<TrialReminder | null>(() => {
    const [serviceId] = serviceIds;
    const subscription = serviceId ? byServiceId.get(serviceId) : undefined;
    if (!serviceId || subscription?.paymentStatus !== PaymentStatus.TRIALING) return null;

    const endsAt = renewalOf(subscription).at;
    if (endsAt === null || endsAt <= now) return null;

    const daysLeft = differenceInCalendarDays(endsAt, now);
    const stage = stageOf(daysLeft);
    if (!stage) return null;

    return { stage, daysLeft, endsAt, serviceId, planLabel: planName(subscription) };
  }, [serviceIds, byServiceId, now]);

  const dismiss = useCallback(() => {
    if (!reminder || reminder.stage === 'lastDay') return;
    const next: Dismissal = {
      serviceId: reminder.serviceId,
      endsAt: reminder.endsAt,
      stage: reminder.stage,
      until: reminder.stage === 'week' ? Date.now() + DAY_MS : null,
    };
    setDismissal(next);
    try {
      localStorage.setItem(DISMISSED_KEY, JSON.stringify(next));
    } catch {
      // Private mode or blocked storage: the banner stays closed until the next load.
    }
  }, [reminder]);

  return {
    reminder: reminder && !isDismissed(reminder, dismissal, now) ? reminder : null,
    dismiss,
  };
};
